import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Share2, Workflow } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { LanguageCode } from "../../utils/translations";
import { SERVICES } from "../services/servicesData";
import { HOME_COPY } from "./homeContent";
import { HOME_THEME } from "./theme";

// Preload all showcase images so slides load fully on first pass (helps mobile).
const preloadAssets = () => {
  if (typeof window === "undefined") return;
  const names = [
    "social",
    "mobile-apps",
    "messaging",
    "dashboard",
    "integration",
    "email",
    "team-2",
    "ai-matrix",
    "ai-circuit",
    "security",
    "code-2",
    "team-laptops",
    "network-globe",
    "servers-2",
    "webdesign",
    "phone-1",
    "servers-1",
  ];
  const base = process.env.PUBLIC_URL || "";
  names.forEach((n) => {
    const img = new Image();
    img.src = `${base}/assets/showcase/${n}.jpg`;
  });
};

interface Slide {
  title: string;
  subtitle: string;
  to: string;
  tag: string;
  icon: LucideIcon;
  image: string;
}

// Relevant photo per capability, in the same order as HOME_COPY.*.capabilities.items.
const CAP_IMAGE_ORDER = [
  "social",
  "mobile-apps",
  "messaging",
  "dashboard",
  "integration",
  "email",
  "team-2",
  "dashboard",
  "ai-matrix",
];

// Relevant photo per service (keyed by slug).
const SERVICE_IMAGE: Record<string, string> = {
  "ai-development": "ai-circuit",
  "ai-agents": "security",
  "api-development": "code-2",
  "crm-integration": "team-laptops",
  "system-integrations": "network-globe",
  "cloud-infrastructure": "servers-2",
  "messaging-services": "messaging",
  "website-development": "webdesign",
  "mobile-app-development": "phone-1",
  "mobile-workflow-automation": "servers-1",
};

const asset = (name: string) =>
  `${process.env.PUBLIC_URL}/assets/showcase/${name}.jpg`;

function buildSlides(lang: LanguageCode): Slide[] {
  const copy = HOME_COPY[lang];

  const capabilitySlides: Slide[] = copy.capabilities.items.map((c, i) => ({
    title: c.title,
    subtitle: c.desc,
    to: c.to,
    tag: c.tag,
    icon: c.category === "Social Media" ? Share2 : Workflow,
    image: asset(CAP_IMAGE_ORDER[i] ?? "ai-circuit"),
  }));

  const serviceSlides: Slide[] = SERVICES.map((s) => {
    const local = copy.serviceSlides[s.slug];
    return {
      title: local?.title ?? s.title,
      subtitle: local?.tagline ?? s.tagline,
      to: `/services/${s.slug}`,
      tag: local?.tag ?? s.group,
      icon: s.icon,
      image: asset(SERVICE_IMAGE[s.slug] ?? "ai-circuit"),
    };
  });

  return [...capabilitySlides, ...serviceSlides];
}

const SLIDE_MS = 5200;

interface HeroShowcaseProps {
  lang: LanguageCode;
}

/**
 * Auto-scrolling showcase used in the hero instead of a single static image.
 * Cycles through every capability and end-to-end service, each with a relevant
 * photo; pauses on hover. Click any slide to open the matching page.
 */
const HeroShowcase: React.FC<HeroShowcaseProps> = ({ lang }) => {
  const navigate = useNavigate();
  const copy = HOME_COPY[lang];
  const slides = buildSlides(lang);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    preloadAssets();
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      SLIDE_MS
    );
    return () => clearInterval(id);
    // Re-arm when the language (and therefore the slide list) changes.
  }, [paused, slides.length, lang]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 30px 60px -20px rgba(15,23,42,0.45)",
        background: HOME_THEME.dark,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          position: "relative",
          zIndex: 2,
          background: HOME_THEME.dark,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.9rem",
          }}
        >
          <Workflow size={16} color="#5EEAD4" /> {copy.hero.showcaseLabel}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            color: "#34D399",
            fontSize: "0.75rem",
            fontWeight: 700,
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#34D399",
              display: "inline-block",
            }}
          />
          {copy.hero.live}
        </span>
      </div>

      {/* Scrolling track */}
      <div style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            transition: "transform 0.9s ease",
            transform: `translateX(-${index * 100}%)`,
          }}
        >
          {slides.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={`${s.tag}-${s.title}-${i}`}
                onClick={() => navigate(s.to)}
                style={{
                  flex: "0 0 100%",
                  minHeight: "340px",
                  boxSizing: "border-box",
                  padding: "26px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  // Relevant image + dark overlay so the text stays readable.
                  background: `linear-gradient(180deg, rgba(15,23,42,0.30) 0%, rgba(15,23,42,0.72) 55%, rgba(15,23,42,0.94) 100%), url(${s.image}) center/cover no-repeat, ${HOME_THEME.dark}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      width: "54px",
                      height: "54px",
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.16)",
                      backdropFilter: "blur(4px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={28} color="#fff" />
                  </div>
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      color: "#5EEAD4",
                      background: "rgba(2,6,23,0.55)",
                      border: "1px solid rgba(94,234,212,0.35)",
                      borderRadius: "999px",
                      padding: "5px 12px",
                      maxWidth: "60%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.tag}
                  </span>
                </div>

                <div>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.2,
                      textShadow: "0 2px 12px rgba(0,0,0,0.5)",
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    style={{
                      margin: "0 0 16px 0",
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                      maxWidth: "420px",
                    }}
                  >
                    {s.subtitle}
                  </p>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                    }}
                  >
                    {copy.hero.explore} <ArrowRight size={15} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          justifyContent: "center",
          padding: "14px",
          flexWrap: "wrap",
        }}
      >
        {slides.map((s, i) => (
          <button
            key={`dot-${i}`}
            type="button"
            aria-label={`Show ${s.title}`}
            onClick={() => setIndex(i)}
            style={{
              width: i === index ? "20px" : "8px",
              height: "8px",
              borderRadius: "999px",
              border: "none",
              padding: 0,
              cursor: "pointer",
              background:
                i === index ? "#5EEAD4" : "rgba(255,255,255,0.25)",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroShowcase;
